// salvarFatura.ts
import { generateReactHelpers } from "@uploadthing/react"
import {
  deleteAnexosByIdentificador,
  buildAnexoPrefix,
  useFaturasStore,
  type SalvarFatura,
  useSalvarFaturas,
  useProgressToast,
} from "@FN"
import { usePrenotaStore } from "@PN/inclusao"
import type { OurFileRouter } from "@/app/api/uploadthing/core"
import { fmt } from "visuais/fmt"

const normalizeText = (text: string) => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[^a-zA-Z0-9\s]/g, " ") // Mantém maiúsculas também
    .replace(/\s+/g, " ") // Normaliza espaços
    .trim();
};

export function useSalvarFatura({
  rec,
  anexos,
  fornecedorFatura,
  selecionados,
  observacao,
  usuario,
  filial,
  table,
  mode,
  faturaZ15,
  form,
  fornecedorSelecionado,
}: any) {
  const resetPrenotaStore = usePrenotaStore((s) => s.actions.reset)
  const resetFaturaStore = useFaturasStore((s) => s.resetFaturaStore)
  const storeUploadFiles = usePrenotaStore((s) => s.aux.uploadFiles) || new Map()
  const { uploadFiles } = generateReactHelpers<OurFileRouter>()
  const { showProgressToast } = useProgressToast()

  const salvarFaturaMutation = useSalvarFaturas({
    onMutate: () => {
      const steps = ["Preparando envio", "Enviando anexos", "Finalizando"]
      return showProgressToast({
        title: "Salvando fatura",
        steps,
        successMessage: "Fatura salva com sucesso!",
      })
    },
    onSuccess: async (_, __, context) => {
      const toastInstance = context as ReturnType<typeof showProgressToast>
      try {
        toastInstance.updateProgress(0)

        let effectiveRec = rec || 0
        let data: any = null

        if (!effectiveRec) {
          const query = new URLSearchParams({
            filial: filial ?? "",
            loja: fornecedorFatura?.A2_LOJA ?? "",
            user: usuario?.login ?? "",
          })
          const res = await fetch(`/api/financeiro/faturas?${query.toString()}`)
          data = await res.json()
          effectiveRec = data?.[0]?.R_E_C_N_O_ || 0
        }

        if (!effectiveRec) throw new Error("REC não retornado")

        const identificador =
          mode === "edicao" && faturaZ15
            ? {
                filial: faturaZ15.Z15_FILIAL,
                fornecedor: faturaZ15.Z15_FORNEC,
                loja: faturaZ15.Z15_LOJA,
                cod: faturaZ15.Z15_COD,
              }
            : {
                filial: data?.[0]?.Z15_FILIAL ?? "999999",
                fornecedor: data?.[0]?.Z15_FORNEC ?? "999999",
                loja: data?.[0]?.Z15_LOJA ?? "99999",
                cod: data?.[0]?.Z15_COD ?? "9999999",
              }

        const prefix = buildAnexoPrefix(identificador)
              
        const filesToUpload: File[] = []
        for (let i = 0; i < anexos.length; i++) {
          const anexo = anexos[i]
          let originalFile: File | undefined

          for (const [, fileProgress] of storeUploadFiles.entries()) {
            if (fileProgress.file?.name === anexo.nomeArquivo) {
              originalFile = fileProgress.file
              break
            }
          }

          if (!originalFile) throw new Error(`Arquivo não encontrado: ${anexo.nomeArquivo}`)

          const ext = anexo.nomeArquivo.split(".").pop()?.toLowerCase() ?? "pdf"
          if (ext !== "pdf") throw new Error(`Apenas PDF permitido: ${anexo.nomeArquivo}`)

          const seq = String(i + 1).padStart(2, "0")
          filesToUpload.push(
            new File([originalFile], `${prefix}_${seq}.${ext}`, {
              type: "application/pdf",
              lastModified: originalFile.lastModified,
            }),
          )
        }

        toastInstance.updateProgress(1)

        if (rec) await deleteAnexosByIdentificador(identificador)

        await uploadFiles("anexoUploader", { files: filesToUpload })

        toastInstance.updateProgress(2)

        resetFaturaStore()
        resetPrenotaStore()
        table?.resetRowSelection()
      } catch (err: any) {
        console.error("Erro ao enviar anexos:", err)
        toastInstance.markAsError(err.message || "Erro ao enviar anexos")
      }
    },
    onError: (err, __, context) => {
      const toastInstance = context as ReturnType<typeof showProgressToast>
      toastInstance?.markAsError("Erro ao salvar fatura")
      console.error("Erro ao salvar fatura:", err)
    },
  })

  const handleSalvar = form.handleSubmit(async () => {
    if (!fornecedorSelecionado || selecionados.length === 0 || anexos.length === 0) return
    if (salvarFaturaMutation.isPending) return

    const body: SalvarFatura = {
      FILIAL: filial,
      REC: rec || 0,
      USER: usuario?.login ?? "",
      OBS: normalizeText(observacao),
      FORNECE: fornecedorFatura!.A2_COD,
      LOJA: fornecedorFatura!.A2_LOJA,
      VALOR: selecionados.reduce((acc: number, f: any) => acc + (f.VALOR || 0), 0),
      TITULOS: selecionados.map((f: any) => ({
        TITULO: f.Z10_CHAVE,
        Z16_NF: f.F1_DOC,
        Z16_SERIE: f.F1_SERIE,
        Z16_FORNE: f.F1_FORNECE,
        Z16_LOJA: f.F1_LOJA,
        Z16_EMISSA: fmt.dates.BR(f.F1_EMISSAO),
        Z16_CHAVE: f.F1_CHVNFE,
        Z16_VALOR: f.VALOR,
      })),
    }

    salvarFaturaMutation.mutate(body)
  })

  return { handleSalvar, salvarFaturaMutation }
}
